import {useQuery} from '@tanstack/react-query'
import {useNavigate, useParams, useSearchParams} from 'react-router-dom'

import {
  MemberListSchemaResponse,
  MemberResponse,
  MemberSchemaResponse,
  PackageDetailResponse,
  PackageDetailSchemaResponse,
  PackagePaymentListResponse,
  PackagePaymentListResponseSchema,
  Responses,
} from '../domain'
import {useMember} from '../hook'

import {useAuthUser} from 'react-auth-kit'
import {toast} from 'react-toastify'
import {API} from 'src/feature/bite/domain'
import {getTimeZone} from 'src/feature/bite/helpers/date'
import useRequestParams from 'src/feature/bite/hook/request'
import {usePackageDetail} from '../hook/package'
import {PurchaseListResponse, PurchaseListSchemaResponse} from '../store'

const endPoints = {
  Segment: 'GetMemberSegments',
  Address: 'GetMemberAddresses',
  Consent: 'GetMemberConcents',
  Transaction: 'GetMemberTransactions',
  PushEngagement: 'GetMemberEngagements',
  EmailEngagement: 'GetMemberEngagements',
  SmsEngagement: 'GetMemberEngagements',
  Packages: 'GetMemberPackages',
  Activities: 'GetMemberPackageActivities',
}

const typeOfFilterKey = {
  Segment: 'CreatedDate', // One Page
  Address: 'CreatedDate', // One Page
  Consent: 'CreatedDate', // One Page
  Transaction: 'Date',
  PushEngagement: 'CreatedOn',
  EmailEngagement: 'CreatedOn',
  SmsEngagement: 'CreatedOn',
  Packages: 'CreatedDate', // One Page
  Activities: 'Date', // One Page
}

export function useGetList() {
  const {auth, headers, errorMessage, signOut} = useRequestParams()
  const {listFilter, listFilterSet, listSet} = useMember()
  const timeZoneOffset = getTimeZone()

  const userID = auth()?.id

  return useQuery(
    ['members', listFilter],
    async () => {
      const Response = await fetch(`${API}/Member/List`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          ...listFilter,
          userID,
          language: headers.Language,
          timeZoneOffSet: timeZoneOffset,
        }),
      })

      if (Response.status === 401) {
        signOut()
      }

      const Data = await Response.json()

      const parse = MemberListSchemaResponse.parse(Data)

      listSet(parse.response)
      listFilterSet({
        ...listFilter,
        paging: {
          currentPageNumber: parse.response.paging.currentPageNumber,
          pageSize: parse.response.paging.pageSize,
        },
      })

      return true
    },
    {
      enabled: true,
      refetchOnWindowFocus: false,
      refetchOnMount: true,
      refetchOnReconnect: false,
      retry: false,
      onSuccess: () => {},
      onError: (error) => {
        toast.error(errorMessage, {
          position: 'top-right',
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: false,
          progress: undefined,
          theme: 'light',
        })
      },
    }
  )
}

export function useGetDetail(memberId: string) {
  const auth = useAuthUser()
  const router = useNavigate()
  const {detail, detailSet} = useMember()
  const {headers, errorMessage, signOut} = useRequestParams()
  const userID = auth()?.id

  return useQuery(
    ['MemberDetail', detail?.memberId],
    async () => {
      if (detail?.memberId) return true
      const timeZoneOffset = getTimeZone()

      const Response = await fetch(`${API}/Member/Detail`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          memberId,
          storeId: '',
          userID,
          language: headers.Language,
          timeZoneOffSet: timeZoneOffset,
        }),
      })

      if (Response.status === 401) {
        signOut()
      }

      const response: MemberResponse = await Response.json()

      const parse = MemberSchemaResponse.parse(response)

      if (parse.responseResult.result === false) {
        toast.error(parse.responseResult.exceptionDetail || 'Error', {
          position: 'top-right',
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: false,
          progress: undefined,
          theme: 'light',
        })
        return false
      }

      if (!parse.response?.member) {
        router('/error/member')
        return true
      }

      detailSet(parse.response.member)
      return true
    },
    {
      enabled: true,
      refetchOnWindowFocus: false,
      refetchOnMount: true,
      refetchOnReconnect: false,
      retry: false,
      onSuccess: () => {},
      onError: (error) => {
        console.log('error', error)
        toast.error(errorMessage, {
          position: 'top-right',
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: false,
          progress: undefined,
          theme: 'light',
        })
      },
    }
  )
}

export function useGetPackageDetail(packageId: string) {
  const auth = useAuthUser()
  const router = useNavigate()
  const {detail, detailSet} = usePackageDetail()
  const {headers, errorMessage, signOut} = useRequestParams()
  const userID = auth()?.id

  return useQuery(
    ['MemberPackageDetail', packageId],
    async () => {
      if (!packageId) return false
      const timeZoneOffset = getTimeZone()

      const Response = await fetch(`${API}/Member/GetMemberPackageDetail`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          id: packageId,
          userID,
          language: headers.Language,
          timeZoneOffSet: timeZoneOffset,
        }),
      })

      if (Response.status === 401) {
        signOut()
      }

      const response: PackageDetailResponse = await Response.json()

      const parse = PackageDetailSchemaResponse.parse(response)

      if (parse.responseResult.result === false) {
        toast.error(parse.responseResult.exceptionDetail || 'Error', {
          position: 'top-right',
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: false,
          progress: undefined,
          theme: 'light',
        })
        return false
      }

      if (!parse.response) {
        router('/error/404')
        return true
      }

      detailSet(parse.response)
      return true
    },
    {
      enabled: true,
      refetchOnWindowFocus: false,
      refetchOnMount: true,
      refetchOnReconnect: false,
      retry: false,
      onSuccess: () => {},
      onError: (error) => {
        console.log('error', error)
        toast.error(errorMessage, {
          position: 'top-right',
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: false,
          progress: undefined,
          theme: 'light',
        })
      },
    }
  )
}

export function useGetPackagePaymentList(packageId: string) {
  const auth = useAuthUser()
  const router = useNavigate()
  const {paymentListSet, paymentListPaging} = usePackageDetail()
  const {headers, errorMessage, signOut} = useRequestParams()
  const userID = auth()?.id

  return useQuery(
    ['MemberPackagePaymentList', packageId, paymentListPaging],
    async () => {
      if (!packageId) return false
      const timeZoneOffset = getTimeZone()

      const Response = await fetch(`${API}/Member/GetMemberPackagePaymentList`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          id: packageId,
          userID,
          language: headers.Language,
          timeZoneOffSet: timeZoneOffset,
          paging: {
            currentPageNumber: paymentListPaging.currentPageNumber || 1,
            pageSize: paymentListPaging.pageSize || 10,
          },
        }),
      })

      if (Response.status === 401) {
        signOut()
      }

      const response: PackagePaymentListResponse = await Response.json()

      const parse = PackagePaymentListResponseSchema.parse(response)

      if (parse.responseResult.result === false) {
        toast.error(parse.responseResult.exceptionDetail || 'Error', {
          position: 'top-right',
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: false,
          progress: undefined,
          theme: 'light',
        })
        return false
      }

      if (!parse.response) {
        router('/error/404')
        return true
      }

      paymentListSet(parse)
      return true
    },
    {
      enabled: true,
      refetchOnWindowFocus: false,
      refetchOnMount: true,
      refetchOnReconnect: false,
      retry: false,
      onSuccess: () => {},
      onError: (error) => {
        console.log('error', error)
        toast.error(errorMessage, {
          position: 'top-right',
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: false,
          progress: undefined,
          theme: 'light',
        })
      },
    }
  )
}

export function useGetTabDetail(memberId: string) {
  const {detailTab, tabName, updateDetail, listFilterMarketing, detail} = useMember()
  const {headers, errorMessage, signOut} = useRequestParams()

  const apiEndPoint = endPoints[tabName]
  const filterKey = typeOfFilterKey[tabName]

  const defaultPagination = {currentPageNumber: 1, pageSize: 5}

  const paging =
    detailTab && detailTab[tabName]?.paging ? detailTab[tabName]?.paging : defaultPagination

  const filter = {
    filterList: listFilterMarketing.filterList,
    orderList: [{type: 2, key: filterKey}],
    paging: {currentPageNumber: paging.currentPageNumber, pageSize: paging.pageSize},
  }

  const timeZoneOffSet = getTimeZone()

  let engagementIndex: number, engagementName: string

  if (tabName === 'SmsEngagement') {
    engagementIndex = 1
    engagementName = 'textMessages'
  }
  if (tabName === 'PushEngagement') {
    engagementIndex = 3
    engagementName = 'pushes'
  }
  if (tabName === 'EmailEngagement') {
    engagementIndex = 2
    engagementName = 'emails'
  }

  const auth = useAuthUser()
  const userID = auth()?.id
  const {memberId: memberIdUrlQuery} = useParams()

  return useQuery(
    ['MemberDetailTab', tabName, filter],
    async () => {
      if (
        tabName == null ||
        tabName === 'Overview' ||
        tabName === 'Transaction' ||
        tabName === 'Activities' ||
        tabName === 'Packages'
      )
        return true

      const Response = await fetch(`${API}/Member/${apiEndPoint}`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          memberId: tabName === 'Consent' ? memberIdUrlQuery : detail?.customerID || memberId,
          customerID: detail?.customerID,
          userID,
          timeZoneOffSet,
          language: headers.Language,
          ...(tabName === 'Consent' && {merchantCustomerID: memberIdUrlQuery}),
          ...filter,
          ...(engagementIndex && {engagementType: engagementIndex}),
        }),
      })

      if (Response.status === 401) {
        signOut()
      }

      const Data = await Response.json()

      const ResponseSchema = Responses[tabName]
      const safeParse = ResponseSchema.safeParse(Data)

      if (!safeParse.success) {
        throw new Error(safeParse.error.message)
      }

      const parse = ResponseSchema.parse(Data)

      if (['SmsEngagement', 'PushEngagement', 'EmailEngagement'].includes(tabName)) {
        updateDetail(
          {
            dataList: parse.response[engagementName],
            paging: parse.response.listPaging,
          },
          tabName
        )
      } else {
        updateDetail(parse.response, tabName)
      }

      return true
    },
    {
      enabled: true,
      refetchOnWindowFocus: false,
      refetchOnMount: true,
      refetchOnReconnect: false,
      retry: false,
      onSuccess: () => {},
      onError: (error: any) => {
        toast.error(errorMessage, {
          position: 'top-right',
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: false,
          progress: undefined,
          theme: 'light',
        })
      },
    }
  )
}

export function useRenewMemberTransactions(memberId: string) {
  const {headers, signOut} = useRequestParams()
  const {detailTabSet, listFilterTrx} = useMember()
  const auth = useAuthUser()
  const userID = auth()?.id

  const timeZoneOffSet = getTimeZone()

  const {data, isLoading, error, refetch} = useQuery(
    ['renewMemberTransactions', memberId, listFilterTrx],
    async () => {
      listFilterTrx.filterList = listFilterTrx.filterList.filter((item) => item.value?.[0] !== '')

      const response = await fetch(`${API}/Member/GetMemberTransactions`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          ...listFilterTrx,
          language: headers.Language,
          userID,
          timeZoneOffSet,
          memberId,
        }),
      })

      if (response.status === 401) {
        signOut()
      }

      const data = await response.json()
      const ResponseSchema = Responses['Transaction']
      const safeParse = ResponseSchema.safeParse(data)

      if (!safeParse.success) {
        throw new Error(safeParse.error.message)
      }

      const parse = ResponseSchema.parse(data)
      detailTabSet((prev) => ({
        ...prev,
        Transaction: parse.response,
      }))
      return true
    },
    {
      onSuccess: () => {},
      onError: (error) => {
        console.error('Error fetching transactions:', error) // Informative error handling
      },
      // Refetch data automatically when the 'handleSubmit' function is called
      refetchOnWindowFocus: false, // Avoid unnecessary refetches (optional)
      refetchInterval: 0, // Disable automatic refetching (optional)
    }
  )

  // Call refetch within 'handleSubmit' to manually trigger data refresh
  const handleRefresh = () => {
    refetch()
  }

  return {data, isLoading, error, handleRefresh}
}

export function useRenewMemberPackages(memberId: string) {
  const {headers, signOut} = useRequestParams()
  const {detailTabSet, listFilterPackage, detail} = useMember()
  const auth = useAuthUser()
  const userID = auth()?.id

  const timeZoneOffSet = getTimeZone()

  const {data, isLoading, error, refetch} = useQuery(
    ['renewMemberPackages', memberId, listFilterPackage],
    async () => {
      listFilterPackage.filterList = listFilterPackage.filterList.filter(
        (item) => item.value?.[0] !== ''
      )

      const response = await fetch(`${API}/Member/GetMemberPackages`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          ...listFilterPackage,
          language: headers.Language,
          userID,
          timeZoneOffSet,
          memberId: detail?.customerID || memberId,
          customerID: detail?.customerID,
        }),
      })

      if (response.status === 401) {
        signOut()
      }

      const data = await response.json()
      const ResponseSchema = Responses['Packages']
      const safeParse = ResponseSchema.safeParse(data)

      if (!safeParse.success) {
        throw new Error(safeParse.error.message)
      }

      const parse = ResponseSchema.parse(data)
      detailTabSet((prev) => ({
        ...prev,
        Packages: parse.response,
      }))
      return true
    },
    {
      onSuccess: () => {},
      onError: (error) => {
        console.error('Error fetching transactions:', error) // Informative error handling
      },
      // Refetch data automatically when the 'handleSubmit' function is called
      refetchOnWindowFocus: false, // Avoid unnecessary refetches (optional)
      refetchInterval: 0, // Disable automatic refetching (optional)
    }
  )

  // Call refetch within 'handleSubmit' to manually trigger data refresh
  const handleRefresh = () => {
    refetch()
  }

  return {data, isLoading, error, handleRefresh}
}

export function useRenewMemberActivities(memberId: string, customerID: string) {
  const {headers, signOut} = useRequestParams()
  const {detailTabSet, listFilterActivities} = useMember()
  const auth = useAuthUser()
  const userID = auth()?.id

  const timeZoneOffSet = getTimeZone()

  const {data, isLoading, error, refetch} = useQuery(
    ['renewMemberActivities', memberId, listFilterActivities],
    async () => {
      listFilterActivities.filterList = listFilterActivities.filterList.filter(
        (item) => item.value?.[0] !== ''
      )

      const response = await fetch(`${API}/Member/GetMemberPackageActivities`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          ...listFilterActivities,
          language: headers.Language,
          userID,
          memberID: customerID,
          timeZoneOffSet,
        }),
      })

      if (response.status === 401) {
        signOut()
      }

      const data = await response.json()
      const ResponseSchema = Responses['Activities']
      const safeParse = ResponseSchema.safeParse(data)

      if (!safeParse.success) {
        throw new Error(safeParse.error.message)
      }

      const parse = ResponseSchema.parse(data)
      detailTabSet((prev) => ({
        ...prev,
        Activities: parse.response,
      }))
      return true
    },
    {
      onSuccess: () => {},
      onError: (error) => {
        console.error('Error fetching transactions:', error) // Informative error handling
      },
      // Refetch data automatically when the 'handleSubmit' function is called
      refetchOnWindowFocus: false, // Avoid unnecessary refetches (optional)
      refetchInterval: 0, // Disable automatic refetching (optional)
    }
  )

  // Call refetch within 'handleSubmit' to manually trigger data refresh
  const handleRefresh = () => {
    refetch()
  }

  return {data, isLoading, error, handleRefresh}
}

export function useGetMembersPurchased(id: string, type: number) {
  const auth = useAuthUser()
  const router = useNavigate()
  const {listPurchaseSet, listPurchaseFilter} = useMember()
  const {headers, errorMessage, signOut} = useRequestParams()
  const userID = auth()?.id
  const merchantID = auth()?.merchantID
  const [searchParams] = useSearchParams()

  return useQuery(
    ['MemberPurchasedList', id, type, listPurchaseFilter],
    async () => {
      if (!id) return false
      const timeZoneOffset = getTimeZone()

      const Response = await fetch(`${API}/Member/GetMemberListByItem`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          itemID: id,
          itemType: searchParams.get('isMembership') === 'true' ? 2 : type,
          userID,
          merchantID,
          orderList: listPurchaseFilter.orderList,
          language: headers.Language,
          filterList: listPurchaseFilter.filterList,
          timeZoneOffSet: timeZoneOffset,
          paging: {
            currentPageNumber: listPurchaseFilter.paging.currentPageNumber || 1,
            pageSize: listPurchaseFilter.paging.pageSize || 10,
          },
        }),
      })

      if (Response.status === 401) {
        signOut()
      }

      const response: PurchaseListResponse = await Response.json()

      const parse = PurchaseListSchemaResponse.parse(response)

      if (parse.responseResult.result === false) {
        toast.error(parse.responseResult.exceptionDetail || 'Error', {
          position: 'top-right',
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: false,
          progress: undefined,
          theme: 'light',
        })
        return false
      }

      if (!parse.response) {
        router('/error/404')
        return true
      }

      listPurchaseSet(parse.response)
      return true
    },
    {
      enabled: true,
      refetchOnWindowFocus: false,
      refetchOnMount: true,
      refetchOnReconnect: false,
      retry: false,
      onSuccess: () => {},
      onError: (error) => {
        console.log('error', error)
        toast.error(errorMessage, {
          position: 'top-right',
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: false,
          progress: undefined,
          theme: 'light',
        })
      },
    }
  )
}
